import {
  BarChart3,
  Briefcase,
  Building2,
  Clock,
  LayoutDashboard,
  LineChart,
  ListTodo,
  LogOut,
  Menu,
  MessageSquare,
  UserCircle,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { BadgePendencias } from '@/components/BadgePendencias';
import { Button, buttonVariants } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { usePendenciasContagem, useTotalNaoLidasChat } from '@/lib/api-hooks';
import { cn } from '@/lib/cn';
import { queryClient } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Layout base do app autenticado.
 *
 * Sprint 05 (D7): sidebar à esquerda em desktop (≥ md) e Sheet (drawer) em
 * mobile. Itens condicionais: "Usuários" só aparece se o usuário tiver
 * papel GESTOR (R1/P14 — UI esconde + backend reforça com 403).
 */

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  somenteGestor?: boolean;
  /** Sprint 18 — se `true`, item mostra contador de pendências ao lado do label. */
  badge?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/os', label: 'OS', icon: Briefcase },
  { to: '/pendencias', label: 'Pendências', icon: ListTodo, badge: true },
  // Sprint 20 — apontamento visível a todos; controle interno por papel.
  { to: '/apontamento', label: 'Apontamento', icon: Clock, somenteGestor: false },
  // Sprint 22 — chat interno. Mesmo mecanismo `badge: true`; a contagem
  // exibida depende do `item.to` (Pendências vs Chat) — ver `NavList`.
  { to: '/chat', label: 'Chat', icon: MessageSquare, badge: true, somenteGestor: false },
  // Sprint 16 — relatórios analíticos só Gestor.
  { to: '/relatorios/usuario', label: 'Rel. por usuário', icon: BarChart3, somenteGestor: true },
  { to: '/relatorios/periodo', label: 'Rel. por período', icon: LineChart, somenteGestor: true },
  { to: '/clientes', label: 'Clientes', icon: Building2, somenteGestor: true },
  { to: '/usuarios', label: 'Usuários', icon: Users, somenteGestor: true },
  { to: '/perfil', label: 'Perfil', icon: UserCircle },
];

function NavList({
  onItemClick,
  contagemChat,
}: {
  onItemClick?: () => void;
  contagemChat: number;
}) {
  const papeis = useAuthStore((s) => s.usuario?.papeis ?? []);
  const isGestor = papeis.includes('GESTOR');
  // Lote D / R-2.12 — endpoint dedicado `/pendencias/contagem` (mesmo
  // queryKey usado pelo <BadgePendencias> do header). Sem requisição
  // adicional + zero overhead de trafegar a lista inteira no polling.
  const { data: contagemRaw } = usePendenciasContagem();
  const contagemPendencias = contagemRaw ?? 0;
  // `contagemChat` vem do Layout (uma única instância de useConversas)
  // para evitar dois `useEffect`s de toast quando o drawer mobile estiver
  // aberto e o sidebar desktop estiver montado em paralelo.

  function contagemDoItem(to: string): number {
    if (to === '/pendencias') return contagemPendencias;
    if (to === '/chat') return contagemChat;
    return 0;
  }

  function testIdDoItem(to: string): string {
    if (to === '/pendencias') return 'nav-badge-pendencias';
    if (to === '/chat') return 'nav-badge-chat';
    return 'nav-badge';
  }

  return (
    <nav className="flex flex-col gap-1">
      {NAV_ITEMS.filter((item) => !item.somenteGestor || isGestor).map((item) => {
        const contagem = item.badge ? contagemDoItem(item.to) : 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onItemClick}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
              )
            }
          >
            <item.icon className="h-4 w-4" />
            <span className="flex-1">{item.label}</span>
            {item.badge && contagem > 0 ? (
              <span
                className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground"
                data-testid={testIdDoItem(item.to)}
              >
                {contagem > 99 ? '99+' : contagem}
              </span>
            ) : null}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function Layout() {
  const navigate = useNavigate();
  const usuario = useAuthStore((s) => s.usuario);
  const logout = useAuthStore((s) => s.logout);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Sprint 22 — uma única chamada para o cache de conversas + toast (S22 §5).
  // Passada como prop para que `NavList` (renderizado 2x em mobile com drawer
  // aberto) não dispare o `useEffect` de toast duas vezes.
  const contagemChat = useTotalNaoLidasChat();

  function handleLogout() {
    logout();
    // Limpa cache react-query — evita que o próximo usuário a fazer login
    // veja brevemente dados do anterior (R-005-adjacente).
    queryClient.clear();
    toast.success('Você saiu.');
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto flex items-center justify-between py-4 gap-4">
          <div className="flex items-center gap-3">
            {/* Menu hamburger só em mobile */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  aria-label="Abrir menu"
                  data-testid="menu-hamburger"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <div className="mt-6">
                  <NavList onItemClick={() => setMobileOpen(false)} contagemChat={contagemChat} />
                </div>
              </SheetContent>
            </Sheet>

            <Link to="/dashboard" className="flex flex-col">
              <span className="text-lg font-semibold">Gestão de Projetos</span>
              {usuario ? (
                <span className="text-xs text-muted-foreground" data-testid="empresa-nome">
                  {usuario.empresa.razaoSocial}
                </span>
              ) : null}
            </Link>
          </div>

          {usuario ? (
            <div className="flex items-center gap-3">
              {/* Sprint 16 — badge de pendências (D15: sem polling). */}
              <BadgePendencias />
              <span className="text-sm hidden sm:inline" data-testid="usuario-nome">
                {usuario.nome}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                aria-label="Sair"
                data-testid="botao-sair"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sair
              </Button>
            </div>
          ) : null}
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Sidebar fixo no desktop */}
        <aside
          className="hidden md:block w-60 border-r bg-card/30 p-4"
          data-testid="sidebar-desktop"
        >
          <NavList contagemChat={contagemChat} />
        </aside>

        <main className="flex-1 container mx-auto py-8 px-4 md:px-8">
          <Outlet />
        </main>
      </div>

      <footer className="border-t bg-card">
        <div className="container mx-auto py-4 text-xs text-muted-foreground">
          Gestão de Projetos — MVP · Sprint 06
        </div>
      </footer>
    </div>
  );
}

/**
 * Re-export do `buttonVariants` para uso em `<Link className={...}>` —
 * mantém DRY se outras páginas precisarem.
 */
export { buttonVariants };
